import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import { Pagination, Response, SelectItemProps } from '@ui/src';
import {
  ListOfWarnings,
  MonitoringRulesWarningReport,
  OrganizationInReport,
  OrganizationsWarningNumberOfAlerts,
  TransactionMonitoringRule,
  WarningsNumberOfAlerts,
} from '../types';
import { warningsReportApi } from './service';
import { TransactionType, PaymentType, RiskScoreRange } from './types';

import ROUTES from '../../../routes';

const getTransactionTypeName = (name: string) => {
  let transactionTypeName: string;

  switch (name) {
    case 'FEE':
      transactionTypeName = 'Fee';
      break;
    case 'INTERNAL_PAYMENT':
      transactionTypeName = 'Internal Payment';
      break;
    case 'NOSTRO':
      transactionTypeName = 'Nostro';
      break;
    case 'GENERAL_LEDGER':
      transactionTypeName = 'General Ledger';
      break;
    case 'INTERNAL':
      transactionTypeName = 'Internal';
      break;
    case 'INCOMING':
      transactionTypeName = 'Incoming';
      break;
    case 'OUTGOING':
      transactionTypeName = 'Outgoing';
      break;
    default:
      transactionTypeName = name;
      break;
  }

  return transactionTypeName;
};

const getWarningEntityAndType = (result: ListOfWarnings) => {
  let warningEntity = '';
  let warningType = '';

  if (
    !result.applicationId &&
    !result.transactionId &&
    result.organizationId &&
    result.warningGroup &&
    !result?.warningGroup?.paymentOrderId
  ) {
    warningEntity = 'ORGANIZATION';
  } else if (
    result.applicationId &&
    !result.organizationId &&
    !result.transactionId &&
    result.warningGroup &&
    !result?.warningGroup?.paymentOrderId
  ) {
    warningEntity = 'APPLICATION';
  } else if (
    !result.applicationId &&
    ((!result.warningGroupId && !result?.warningGroup?.paymentOrderId) ||
      (result.warningGroupId && !result?.warningGroup?.paymentOrderId)) &&
    result.transactionId
  ) {
    warningEntity = 'TRANSACTION';
  } else if (
    !result.transactionId &&
    !result.applicationId &&
    result.warningGroup &&
    result?.warningGroup?.paymentOrderId
  ) {
    warningEntity = 'PAYMENT_ORDER';
  }

  if (
    (warningEntity === 'ORGANIZATION' || warningEntity === 'APPLICATION') &&
    result.type === 'BLACKLIST'
  ) {
    warningType = 'Customers block lists ongoing monitoring';
  } else if (warningEntity === 'TRANSACTION' && result.type === 'BLACKLIST') {
    warningType = 'Transactions block lists monitoring';
  } else if (warningEntity === 'TRANSACTION' && result.type === 'SCORING') {
    warningType = 'Transactions risk scoring';
  } else if (warningEntity === 'TRANSACTION' && result.type === 'MONITORING') {
    warningType = 'Transactions monitoring rules';
  } else if (warningEntity === 'PAYMENT_ORDER' && result.type === 'BLACKLIST') {
    warningType = 'Payment orders block lists monitoring';
  } else if (
    warningEntity === 'PAYMENT_ORDER' &&
    result.type === 'MONITORING'
  ) {
    warningType = 'Payment orders monitoring rules';
  } else if (warningEntity === 'TRANSACTION' && result.type === 'AGREEMENT') {
    warningType = 'Agreements';
  }

  return { warningType, warningEntity };
};

export interface WarningsReportState {
  selectedTotalWarnings?: number;
  transactionTypes?: SelectItemProps[];
  paymentTypes?: SelectItemProps[];
  selectedOrganizations?: SelectItemProps[];
  warningsNumbersOfAlerts?: WarningsNumberOfAlerts;
  listOfWarnings?: ListOfWarnings[];
  listOfWarningsPagination?: Pagination;
  errorsList?: string;
  riskScoreRanges?: RiskScoreRange[];
  warningDetails?: ListOfWarnings;
  organizationWarnings?: OrganizationsWarningNumberOfAlerts;
  organizationTotalWarnings?: number;
  organizationWarningDetails?: WarningsNumberOfAlerts;
  blacklists?: SelectItemProps[];
  monitoringRulesWarningsReports?: MonitoringRulesWarningReport;
  organizationMonitoringRulesWarningsReports?: MonitoringRulesWarningReport;
}

const initialState: WarningsReportState = {};

export const warningsReportSlice = createSlice({
  name: 'warningsReport',
  initialState,
  reducers: {
    setFilteredWarningsReportTotal: (
      state,
      action: PayloadAction<number | undefined>,
    ) => {
      state.selectedTotalWarnings = action.payload;
    },
    setSelectedOrganizations: (
      state,
      action: PayloadAction<SelectItemProps[]>,
    ) => {
      state.selectedOrganizations = action.payload;
    },
    setOrganizationsWarningDetails: (
      state,
      action: PayloadAction<WarningsNumberOfAlerts>,
    ) => {
      state.organizationWarningDetails = action.payload;
    },
    clearState: (state) => {
      state.selectedTotalWarnings = undefined;
      state.transactionTypes = undefined;
      state.paymentTypes = undefined;
      state.warningsNumbersOfAlerts = undefined;
      state.monitoringRulesWarningsReports = undefined;
    },
    clearOrganizationsWarningsState: (state) => {
      state.organizationWarnings = undefined;
      state.organizationTotalWarnings = undefined;
    },
    clearOrganizationsWarningDetailsState: (state) => {
      state.organizationWarningDetails = undefined;
      state.organizationMonitoringRulesWarningsReports = undefined;
    },
    clearListOfWarningsState: (state) => {
      state.listOfWarnings = undefined;
      state.listOfWarningsPagination = undefined;
      state.errorsList = undefined;
    },
    clearWarningDetails: (state) => {
      state.warningDetails = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      warningsReportApi.endpoints.getWarningReportsDictionaries.matchFulfilled,
      (
        state,
        action: PayloadAction<{
          transactionTypes: Response<TransactionType>;
          paymentTypes: Response<PaymentType>;
          riskScoreRanges: Response<RiskScoreRange>;
          blacklists: Response<{ id: string; name: string }>;
        }>,
      ) => {
        const { transactionTypes, paymentTypes, riskScoreRanges, blacklists } =
          action.payload;

        if (transactionTypes) {
          state.transactionTypes = transactionTypes?.data?.edges
            ?.filter((item) => item.node.name !== 'CURRENCY_EXCHANGE')
            ?.map((item) => ({
              name: getTransactionTypeName(item.node.name),
              value: item.node.id,
            }));
        }

        if (paymentTypes) {
          state.paymentTypes = paymentTypes?.data?.edges?.map((item) => ({
            name: item.node.name,
            value: item.node?.paymentType,
          }));
        }

        if (riskScoreRanges) {
          state.riskScoreRanges = riskScoreRanges.data.edges
            .map((item) => item.node)
            .sort((a, b) => a.rangeStart - b.rangeStart);
        }

        if (blacklists) {
          state.blacklists = blacklists?.data?.edges?.map((item) => ({
            name: item.node.name,
            value: item.node.id,
          }));
        }
      },
    );
    builder.addMatcher(
      warningsReportApi.endpoints.getWarningsReport.matchFulfilled,
      (state, action: PayloadAction<{ data: WarningsNumberOfAlerts }>) => {
        state.warningsNumbersOfAlerts = action.payload.data;
      },
    );
    builder.addMatcher(
      warningsReportApi.endpoints.getMonitoringRulesWarningsReport
        .matchFulfilled,
      (
        state,
        action: PayloadAction<{ data: MonitoringRulesWarningReport }> & {
          meta: { arg: { originalArgs: string } };
        },
      ) => {
        if (!action.meta.arg.originalArgs?.includes('cursor')) {
          state.monitoringRulesWarningsReports = action.payload.data;
        } else if (
          action.meta.arg.originalArgs?.includes('cursor') &&
          state.monitoringRulesWarningsReports
        ) {
          const nextMonitoringRulesWarningsReports = {
            ...state.monitoringRulesWarningsReports,
          };

          const nextRules = (
            [] as {
              node: TransactionMonitoringRule;
              warningsTotal: number;
              filteredWarningsTotal: number;
            }[]
          ).concat(
            nextMonitoringRulesWarningsReports.monitoringsRulesTotals
              .monitoringsRules,
          );

          action.payload.data.monitoringsRulesTotals.monitoringsRules.forEach(
            (item) => {
              nextRules.push(item);
            },
          );

          nextMonitoringRulesWarningsReports.monitoringsRulesTotals = {
            pageInfo: action.payload.data.monitoringsRulesTotals.pageInfo,
            monitoringsRules: nextRules,
          };

          nextMonitoringRulesWarningsReports.filteredTotal =
            action.payload.data.filteredTotal;
          nextMonitoringRulesWarningsReports.total = action.payload.data.total;

          state.monitoringRulesWarningsReports =
            nextMonitoringRulesWarningsReports;
        }
      },
    );
    builder.addMatcher(
      warningsReportApi.endpoints.getOrganizationMonitoringRulesWarningsReport
        .matchFulfilled,
      (
        state,
        action: PayloadAction<{ data: MonitoringRulesWarningReport }> & {
          meta: { arg: { originalArgs: { id: string; query: string } } };
        },
      ) => {
        if (!action.meta.arg.originalArgs?.query?.includes('cursor')) {
          state.organizationMonitoringRulesWarningsReports =
            action.payload.data;
        } else if (
          action.meta.arg.originalArgs?.query?.includes('cursor') &&
          state.organizationMonitoringRulesWarningsReports
        ) {
          const nextMonitoringRulesWarningsReports = {
            ...state.organizationMonitoringRulesWarningsReports,
          };

          const nextRules = (
            [] as {
              node: TransactionMonitoringRule;
              warningsTotal: number;
              filteredWarningsTotal: number;
            }[]
          ).concat(
            nextMonitoringRulesWarningsReports.monitoringsRulesTotals
              .monitoringsRules,
          );

          action.payload.data.monitoringsRulesTotals.monitoringsRules.forEach(
            (item) => {
              nextRules.push(item);
            },
          );

          nextMonitoringRulesWarningsReports.monitoringsRulesTotals = {
            pageInfo: action.payload.data.monitoringsRulesTotals.pageInfo,
            monitoringsRules: nextRules,
          };

          nextMonitoringRulesWarningsReports.filteredTotal =
            action.payload.data.filteredTotal;
          nextMonitoringRulesWarningsReports.total = action.payload.data.total;

          state.organizationMonitoringRulesWarningsReports =
            nextMonitoringRulesWarningsReports;
        }
      },
    );
    builder.addMatcher(
      warningsReportApi.endpoints.getWarningById.matchFulfilled,
      (
        state,
        action: PayloadAction<{ data: ListOfWarnings }> & {
          meta: { arg: { originalArgs: string } };
        },
      ) => {
        const nextWarningDetails = { ...action.payload.data };

        const { warningEntity, warningType } = getWarningEntityAndType(
          action.payload.data,
        );

        nextWarningDetails.warningEntity = warningEntity;
        nextWarningDetails.warningType = warningType;

        state.warningDetails = nextWarningDetails;
      },
    );
    builder.addMatcher(
      warningsReportApi.endpoints.getOrganizationsWarningsReport.matchFulfilled,
      (state, action: PayloadAction<{ data: WarningsNumberOfAlerts }>) => {
        state.organizationTotalWarnings = action.payload.data.totals.total;

        state.organizationWarnings = {
          ...action.payload.data.organizationsTotals,
          organizations:
            action?.payload?.data?.organizationsTotals?.organizations?.map(
              (item) => {
                const result = { ...item.node };

                result.totalWarnings = item.reports.natural.total;
                result.percentOfTotal = item.reports.relative.total;

                result.customersBlockListOngoingMonitoring =
                  item.reports?.natural.customersBlockListOngoingMonitoring;
                result.paymentsOrdersBlocklistsMonitoring =
                  item.reports?.natural.paymentsOrdersBlocklistsMonitoring;
                result.paymentsOrdersMonitoringRules =
                  item.reports?.natural.paymentsOrdersMonitoringRules;
                result.transactionsAgreements =
                  item.reports?.natural.transactionsAgreements;
                result.transactionsBlockListsMonitoring =
                  item.reports?.natural.transactionsBlockListsMonitoring;
                result.transactionsMonitoringRules =
                  item.reports?.natural.transactionsMonitoringRules;
                result.transactionsRiskScoringMonitoring =
                  item.reports?.natural.transactionsRiskScoringMonitoring;

                result.url = generatePath(
                  ROUTES.WARNING_REPORT_CUSTOMERS_DETAILS,
                  { organizationId: result.id },
                );

                const name =
                  result.type === 'CORPORATE'
                    ? (result.companyName as string)
                    : `${result.firstName} ${result.lastName}`;

                result.name = name;

                return { node: result, reports: item.reports };
              },
            ) as OrganizationInReport[],
        } as OrganizationsWarningNumberOfAlerts;
      },
    );
    builder.addMatcher(
      warningsReportApi.endpoints.getListOfWarnings.matchPending,
      (state) => {
        state.errorsList = undefined;
      },
    );
    builder.addMatcher(
      warningsReportApi.endpoints.getListOfWarnings.matchRejected,
      (state, action: any) => {
        state.listOfWarnings = undefined;
        state.listOfWarningsPagination = undefined;

        if (action?.payload.data) {
          if (Array.isArray(action?.payload.data?.message)) {
            state.errorsList = action?.payload.data?.message
              ?.map((item: { value: string }) => item.value)
              .join('; ');
          } else {
            state.errorsList = action?.payload.data?.error;
          }
        }
      },
    );
    builder.addMatcher(
      warningsReportApi.endpoints.getListOfWarnings.matchFulfilled,
      (state, action: PayloadAction<Response<ListOfWarnings>>) => {
        state.listOfWarnings = action.payload.data.edges?.map((item) => {
          const result = { ...item.node, actions: item.node.action };

          const { warningEntity, warningType } =
            getWarningEntityAndType(result);

          result.warningEntity = warningEntity;
          result.warningType = warningType;
          result.url = generatePath(ROUTES.WARNING_REPORT_LIST_DETAILS, {
            warningId: result.id,
          });
          result.riskScoreFrom = result.riskScore;

          return result;
        });

        state.listOfWarningsPagination = action.payload.data.pageInfo;
      },
    );
  },
});

export const {
  setFilteredWarningsReportTotal,
  clearState,
  setSelectedOrganizations,
  clearListOfWarningsState,
  clearWarningDetails,
  clearOrganizationsWarningsState,
  clearOrganizationsWarningDetailsState,
  setOrganizationsWarningDetails,
} = warningsReportSlice.actions;

export default warningsReportSlice.reducer;
